import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { SkipDirection } from './hooks/useSkipIndicator';
import { SKIP_INDICATOR_ANIMATION_DURATION_MILLISECONDS } from '@/constants/video-player';

interface SkipIndicatorProps {
    direction: SkipDirection;
    accumulatedSeconds: number;
    isVisible: boolean;
}

const ANIMATION_DURATION =
    SKIP_INDICATOR_ANIMATION_DURATION_MILLISECONDS / 1000;

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            duration: 0.2,
        },
    },
    exit: {
        opacity: 0,
        transition: {
            duration: 0.1,
        },
    },
};

const slidingCaretVariants = {
    initial: (isBackward: boolean) => ({
        opacity: 1,
        x: isBackward ? 15 : -15,
    }),
    animate: {
        opacity: 1,
        x: 0,
        transition: {
            duration: ANIMATION_DURATION,
            ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
        },
    },
    exit: {
        opacity: 0,
        transition: {
            duration: 0.1,
        },
    },
};

const innerNumberVariants = {
    initial: { opacity: 0 },
    animate: {
        opacity: 1,
        transition: {
            duration: ANIMATION_DURATION * 0.6,
            ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
        },
    },
};

function formatSkipTime(seconds: number, direction: SkipDirection): string {
    const sign = direction === SkipDirection.Backward ? '-' : '+';
    return `${sign} ${seconds}`;
}

export const SkipIndicator: React.FC<SkipIndicatorProps> = ({
    direction,
    accumulatedSeconds,
    isVisible,
}) => {
    const isBackward = direction === SkipDirection.Backward;
    const Icon = isBackward ? CaretLeftIcon : CaretRightIcon;

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className={`absolute top-1/2 -translate-y-1/2 z-50 flex items-center gap-3 ${
                        isBackward ? 'left-4' : 'right-4'
                    }`}
                    style={{
                        flexDirection: isBackward ? 'row' : 'row-reverse',
                    }}
                >
                    <div className="relative flex items-center justify-center min-w-[1.5rem]">
                        <motion.div
                            key={`caret-${accumulatedSeconds}`}
                            custom={isBackward}
                            variants={slidingCaretVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            className="flex items-center justify-center"
                        >
                            <Icon
                                className="w-5 h-5 text-white-100 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
                                weight="bold"
                            />
                        </motion.div>
                    </div>

                    <motion.div
                        key={accumulatedSeconds}
                        variants={innerNumberVariants}
                        initial="initial"
                        animate="animate"
                        className="text-white-100 text-base font-geist-mono font-bold whitespace-nowrap drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] translate-y-px"
                    >
                        <span className="font-geist-mono">
                            {formatSkipTime(accumulatedSeconds, direction)}
                        </span>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
